// src/analysis/sessionAnalysis/zoneDisplay/recalibratedZoneMetrics.ts
import type { PreprocessedDataPoint } from '../../../processors/lapComparison/types'
import type { RecalibratedLap } from './recalibratedLapPoints'
import { MS_PER_S } from '../../../utils/timeUnits'

/**
 * One lap's zone metrics measured over the zone gates on its RECALIBRATED
 * points (pass 26, extending pass 25's time-only record): the same physical
 * window for every lap. `apexDistance` is zone-relative in the reference
 * frame — the argmin of the same scan that produced `minSpeed`, so value
 * and location can never disagree about their frame.
 */
export interface RecalibratedZoneLapMetrics {
  timeMs: number
  entrySpeed: number
  minSpeed: number
  exitSpeed: number
  apexDistance: number
}

/**
 * Measure one lap on its recalibrated points, or null when the lap cannot
 * be measured (<2 in-window points, or non-positive elapsed time —
 * defensive against locally non-monotonic GPS-matched distances).
 *
 * Time, min speed, and apex use [startDistance, endDistance]. Entry speed
 * is interpolated at `entryGate` (default startDistance) — the
 * display-window start, which for cornering zones is the detected turn-in
 * up to ~80m BEFORE the zone (owner: "entry means turn-in"); for
 * braking/acceleration zones it equals the zone start. Exit speed is at
 * endDistance.
 *
 * Gates are linearly interpolated (binding, spec §4.1): the first
 * in-window point can be one GPS sample (~2-5m) late, which injects
 * gate-value noise of the same order as the differences displayed. Raw
 * points are only a degraded fallback when a gate has no bracketing pair.
 */
export function recalibratedZoneLapMetrics(
  points: PreprocessedDataPoint[],
  startDistance: number,
  endDistance: number,
  entryGate: number = startDistance
): RecalibratedZoneLapMetrics | null {
  const inWindow = points.filter(
    (p) => p.distance >= startDistance && p.distance <= endDistance
  )
  if (inWindow.length < 2) return null

  const entryTime =
    interpolateAtGate(points, startDistance, 'first', 'time') ??
    inWindow[0].time
  const exitTime =
    interpolateAtGate(points, endDistance, 'last', 'time') ??
    inWindow[inWindow.length - 1].time
  const timeMs = Math.round((exitTime - entryTime) * MS_PER_S)
  if (timeMs <= 0) return null

  const entrySpeed =
    interpolateAtGate(points, entryGate, 'first', 'speed') ??
    points.find((p) => p.distance >= entryGate)?.speed ??
    inWindow[0].speed
  const exitSpeed =
    interpolateAtGate(points, endDistance, 'last', 'speed') ??
    inWindow[inWindow.length - 1].speed

  let minSpeed = Infinity
  let apexDistance = 0
  for (const p of inWindow) {
    if (p.speed < minSpeed) {
      minSpeed = p.speed
      apexDistance = p.distance - startDistance
    }
  }

  return { timeMs, entrySpeed, minSpeed, exitSpeed, apexDistance }
}

/**
 * Linear interpolation of `field` over `distance` at the gate, scanning
 * consecutive pairs in lap order. GPS-matched distances can jitter locally,
 * so a gate may be bracketed more than once: the entry gate takes the FIRST
 * bracketing pair, the exit gate the LAST. Null when no pair brackets the
 * gate.
 *
 * Both directions early-exit on their first hit — 'first' scans forward and
 * returns on the first bracketing pair, 'last' scans BACKWARD and returns on
 * the first bracketing pair it meets. This is not a monotonicity assumption:
 * "the last pair found by a forward scan" and "the first pair found by a
 * backward scan" are, by construction, the same pair — the one at the
 * largest index i whose (points[i], points[i+1]) brackets the gate. The
 * forward scan finds it by keeping the running max index that matches; the
 * backward scan finds the same max index directly, just from the other end.
 * Local jitter (a gate bracketed more than once, in any order) changes which
 * index that is, but never which scan direction finds it — so both give
 * byte-identical results while the backward scan can stop as soon as it
 * lands on it, instead of walking the rest of the array unconditionally.
 */
function interpolateAtGate(
  points: PreprocessedDataPoint[],
  gate: number,
  which: 'first' | 'last',
  field: 'time' | 'speed'
): number | null {
  const interpolate = (
    a: PreprocessedDataPoint,
    b: PreprocessedDataPoint
  ): number => {
    const span = b.distance - a.distance
    const t = span > 0 ? (gate - a.distance) / span : 0
    return a[field] + t * (b[field] - a[field])
  }

  if (which === 'first') {
    for (let i = 0; i < points.length - 1; i++) {
      const a = points[i]
      const b = points[i + 1]
      if (a.distance <= gate && gate <= b.distance) return interpolate(a, b)
    }
    return null
  }

  for (let i = points.length - 2; i >= 0; i--) {
    const a = points[i]
    const b = points[i + 1]
    if (a.distance <= gate && gate <= b.distance) return interpolate(a, b)
  }
  return null
}

/**
 * Per-zone, per-lap recalibrated zone metrics keyed by lapId, in zone order.
 *
 * ATOMIC per zone (pass-25 rule, unchanged): the map covers every id in
 * requiredLapIds, or the zone's entry is null — never partial. A comparison
 * table mixing recalibrated and odometer values across laps would recreate
 * the frame-drift lie in subtler form, so callers fall back wholesale per
 * zone. requiredLapIds is the analysis's lapIds array (laps can be dropped
 * between the input Lap[] and the analysis set).
 */
export function computeRecalibratedZoneMetrics(
  recalibratedLaps: RecalibratedLap[],
  zones: { startDistance: number; endDistance: number; entryGate?: number }[],
  requiredLapIds: string[]
): Array<Map<string, RecalibratedZoneLapMetrics> | null> {
  return zones.map((zone) => {
    if (requiredLapIds.length === 0) return null
    const metrics = new Map<string, RecalibratedZoneLapMetrics>()
    for (const lap of recalibratedLaps) {
      const m = recalibratedZoneLapMetrics(
        lap.points,
        zone.startDistance,
        zone.endDistance,
        zone.entryGate
      )
      if (m !== null) metrics.set(lap.lapId, m)
    }
    return requiredLapIds.every((id) => metrics.has(id)) ? metrics : null
  })
}
